import { useEffect, useRef, useState } from 'react';

import { useMiniAppSDK } from '@context/SDKConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks/index.ts';

import { VERSION_0_3 } from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext.tsx';
import { ERROR_CODE_MESSAGE_MAP } from '@/features/method-tester/constants';
import { OPERATION_KEY } from '@/features/method-tester/types/SDKCallResult.ts';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Heading } from '@asphalt-react/typography';
import { GetConsentResult } from '@gopay/miniapp-client';

export const CameraConsent = () => {
  const {
    isLoading,
    response: cameraConsent,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
  } = useSDKHandler<GetConsentResult>();

  const sdk = useMiniAppSDK();
  const { version } = useJsBridgeVersion();
  const [selectedErrorCode, setSelectedErrorCode] = useState<number | null>(
    null
  );
  const resetSelection = () => setSelectedErrorCode(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasCameraPermission, setHasCameraPermission] =
    useState<boolean>(false);
  const [streamState, setStreamState] = useState<MediaStream | null>(null);

  const getCameraConsent = async () => {
    try {
      const options = {
        ...(selectedErrorCode && {
          error: {
            operation_key: OPERATION_KEY.CAMERA,
            code: selectedErrorCode,
          },
        }),
      };
      if (version === VERSION_0_3) {
        await invokeSDKActionV0_3(() => sdk.getConsent('CAMERA'), options);
      } else {
        await invokeSDKActionV1('GPConsent', 'getUserConsent', {
          consent_name: 'CAMERA',
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (streamState && videoRef.current) {
      videoRef.current.srcObject = streamState;
      videoRef.current.play().catch(console.error);
    }

    return () => {
      if (streamState) {
        streamState.getTracks().forEach((track) => track.stop());
        console.log('Camera stream stopped');
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [streamState]);

  /* This function will open camera irrespective if the native has the permission*/
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      streamRef.current = stream;
      setStreamState(stream);
      setHasCameraPermission(true);
    } catch (error) {
      setErrorMessage(
        `User may or may not have consent but camera permission is denied`
      );
      setHasCameraPermission(false);
      console.error('Error while starting the camera', error);
    }
  };

  return (
    <>
      <Card outlined={true}>
        <div className="flex w-full justify-between">
          <Heading h6>Camera Consent</Heading>

          <Button size="xs" onClick={getCameraConsent} disabled={isLoading}>
            Get Camera Consent
          </Button>
        </div>
        <div className="flex gap-4">
          {ERROR_CODE_MESSAGE_MAP.camera.map(({ code, message }) => (
            <label key={code} className="flex items-center gap-1">
              <input
                type="radio"
                name="ERROR_CODE"
                value={code}
                checked={selectedErrorCode === code}
                onChange={() => setSelectedErrorCode(code)}
              />
              {message}
            </label>
          ))}
        </div>
        <Button size="xs" onClick={resetSelection}>
          Reset Error Code
        </Button>
        {isLoading && <Loader />}
        {!isLoading && cameraConsent && (
          <div className="mt-2 w-full">
            <pre
              data-testid="camera-consent-response"
              className="overflow-auto text-sm bg-gray-100 p-2 rounded"
            >
              {JSON.stringify(cameraConsent, null, 2)}
            </pre>
          </div>
        )}

        <div className="d-flex py-2">
          <Button
            data-testid="open-camera-button"
            size="xs"
            onClick={openCamera}
          >
            Click To Open Camera
          </Button>
        </div>
        {hasCameraPermission && (
          <video
            data-testid="camera-video"
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full"
          />
        )}
        {errorMessage && <div data-testid="camera-error">{errorMessage}</div>}
      </Card>
    </>
  );
};
